// Serial / IMEI capture helpers driven by the SKU master `weightData` block:
//   icdflag + icdlabel1..5 -> inbound (PO, ASN)
//   ocdflag + ocdlabel1..5 -> outbound (Sales Order)
//
// SKU Packaging UOM Catch Data slots:
//   label1 = Serial No  -> required
//   label2 = IMEI       -> required
//   label3..5 = Other   -> optional (shown only when configured; no *)

const KNOWN_LABELS = {
  SR: { key: "serialNo", label: "Serial No" },
  SN: { key: "serialNo", label: "Serial No" },
  SERIAL: { key: "serialNo", label: "Serial No" },
  IEMI: { key: "imeiNo", label: "MAC Address" },
  IMEI: { key: "imeiNo", label: "IMEI No" },
};

const DEFAULT_LABELS = [
  { code: "SR", required: true },
  { code: "IMEI", required: true },
];

export const OTHER_COLUMN = { key: "other", label: "Other", required: false };

const isOptionalCode = (code) => {
  const u = String(code || "")
    .toUpperCase()
    .trim();
  return u === "OTHER" || u.startsWith("OTHER");
};

/** label1/2 mandatory; label3-5 optional (matches SKU Catch Data fields). */
const pickLabelEntries = (weightData, prefix) =>
  [1, 2, 3, 4, 5]
    .map((i) => {
      const v = weightData?.[`${prefix}label${i}`];
      if (typeof v !== "string" || v.trim() === "") return null;
      return { code: v.trim(), required: i <= 2, slot: i };
    })
    .filter(Boolean);

export function getSerialConfig(skuRow) {
  const w = skuRow?.weightData || {};
  return {
    inboundEnabled: Number(w.icdflag) === 1,
    inboundLabels: pickLabelEntries(w, "icd"),
    outboundEnabled: Number(w.ocdflag) === 1,
    outboundLabels: pickLabelEntries(w, "ocd"),
  };
}

const toKey = (code) => {
  const cleaned = String(code).replace(/[^a-zA-Z0-9]+/g, " ").trim().toLowerCase();
  return cleaned.replace(/ (\w)/g, (_, c) => c.toUpperCase()) || "field";
};

const normalizeLabelEntry = (entry) => {
  if (entry == null) return null;
  if (typeof entry === "string") {
    const code = entry.trim();
    if (!code) return null;
    const known = !!KNOWN_LABELS[code.toUpperCase()];
    return { code, required: known ? true : !isOptionalCode(code) };
  }
  const code = String(entry.code ?? entry.label ?? "").trim();
  if (!code) return null;
  if (typeof entry.required === "boolean") return { code, required: entry.required };
  const known = !!KNOWN_LABELS[code.toUpperCase()];
  return { code, required: known ? true : !isOptionalCode(code) };
};

/** Columns shown in the capture modal: one per configured SKU label (no auto "Other"). */
export function buildSerialColumns(labels = []) {
  const source = labels.length ? labels : DEFAULT_LABELS;
  const seen = new Set();
  const cols = [];
  source.forEach((raw) => {
    const entry = normalizeLabelEntry(raw);
    if (!entry) return;
    const known = KNOWN_LABELS[String(entry.code).toUpperCase()];
    const col = known
      ? { ...known, code: entry.code, required: entry.required }
      : { key: toKey(entry.code), label: entry.code, code: entry.code, required: entry.required };
    if (seen.has(col.key)) return;
    seen.add(col.key);
    cols.push(col);
  });
  return cols;
}

/** Same as `buildSerialColumns`, but falls back to the keys of already-saved rows (edit mode). */
export function resolveSerialColumns(labels, existingRows = []) {
  if (labels && labels.length) return buildSerialColumns(labels);
  const keys = new Set();
  (existingRows || []).forEach((r) => Object.keys(r || {}).forEach((k) => keys.add(k)));
  if (!keys.size) return buildSerialColumns([]);
  const byKey = Object.values(KNOWN_LABELS).reduce((acc, c) => ({ ...acc, [c.key]: c }), {});
  byKey[OTHER_COLUMN.key] = OTHER_COLUMN;
  return [...keys].map((k) => {
    const base = byKey[k] || { key: k, label: k };
    const required =
      typeof base.required === "boolean"
        ? base.required
        : k === OTHER_COLUMN.key || isOptionalCode(k)
          ? false
          : true;
    return { ...base, required };
  });
}

export const toSerialQty = (qty) => {
  const n = Math.floor(Number(qty));
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export function resizeSerialRows(rows = [], qty) {
  const n = toSerialQty(qty);
  const out = (rows || []).slice(0, n).map((r) => ({ ...r }));
  while (out.length < n) out.push({});
  return out;
}

const isFilled = (v) => v !== undefined && v !== null && String(v).trim() !== "";

export const isSerialRowComplete = (row, columns) =>
  columns.filter((c) => c.required).every((c) => isFilled(row?.[c.key]));

export const countCapturedSerials = (rows = [], qty, columns) =>
  (rows || []).slice(0, toSerialQty(qty)).filter((r) => isSerialRowComplete(r, columns)).length;

/** Rows to persist: trimmed to the current qty, fully-empty rows dropped. */
export const compactSerialRows = (rows = [], qty) =>
  (rows || [])
    .slice(0, toSerialQty(qty))
    .filter((r) => Object.values(r || {}).some(isFilled));
